import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { getAssistant } from "../../features/assistant/assistantSlice";
import { getIcon } from "../../constants/constants";
import { getPlatformWebhookConfig } from "../AssistantDetails/Assistants.utils";
import {
  getWebhookConfiguration,
  saveWebhookConfiguration,
} from "../../features/webhook/webhookSlice";
import { toast } from "react-toastify";

const ConfigureWebhook = () => {
  const navigate = useNavigate();
  const { assistantId, settingId } = useParams();
  const [platformName, setPlatformName] = useState("");
  const { configuration, loading } = useSelector((state) => state.webhook);
  const [webhookEvents, setWebhookEvents] = useState({});

  const dispatch = useDispatch();

  useEffect(() => {
    const fetchAssistant = async () => {
      try {
        const data = await dispatch(getAssistant(assistantId)).unwrap();
        setPlatformName(data?.platform?.type?.toLowerCase() || "");
      } catch (error) {
        console.error(error);
      }
    };

    fetchAssistant();
  }, [dispatch, assistantId]);

  useEffect(() => {
    dispatch(getWebhookConfiguration(settingId));
  }, [dispatch, settingId]);

  let iconSrc = getIcon(platformName);
  const webhookConfig = useMemo(
    () => getPlatformWebhookConfig(platformName),
    [platformName],
  );

  const events = webhookConfig.events;
  const configuredEvents = useMemo(() => {
    const savedEvents = configuration?.data?.events;

    if (!Array.isArray(savedEvents)) return {};

    return savedEvents.reduce((acc, event) => {
      if (event?.value) acc[event.value] = event;

      return acc;
    }, {});
  }, [configuration]);

  useEffect(() => {
    const initialEvents = events.reduce((acc, event) => {
      const savedEvent = configuredEvents[event.value];

      acc[event.value] = {
        enabled: savedEvent?.enabled ?? event.enabled ?? false,
        message: savedEvent?.message ?? event.message ?? "",
      };

      return acc;
    }, {});

    setWebhookEvents(initialEvents);
  }, [events, configuredEvents]);

  const handleEventToggle = (eventValue) => {
    setWebhookEvents((prev) => ({
      ...prev,
      [eventValue]: {
        ...prev[eventValue],
        enabled: !prev[eventValue]?.enabled,
      },
    }));
  };

  const handleMessageChange = (eventValue, message) => {
    setWebhookEvents((prev) => ({
      ...prev,
      [eventValue]: {
        ...prev[eventValue],
        message,
      },
    }));
  };

  const insertVariable = (eventValue, variable) => {
    const value = `{{${variable}}}`;

    setWebhookEvents((prev) => ({
      ...prev,
      [eventValue]: {
        ...prev[eventValue],
        message: `${prev[eventValue]?.message || ""}${value}`,
      },
    }));
  };

  const renderPreview = (message) => {
    return message
      .replaceAll("{{user}}", "John")
      .replaceAll("{{due_date}}", "Sep 2");
  };

  const handleSave = async () => {
    try {
      const payload = {
        settingId,
        events: Object.entries(webhookEvents).map(([value, config]) => ({
          value,
          enabled: config.enabled,
          message: config.message ?? "",
        })),
      };

      dispatch(saveWebhookConfiguration(payload));
      toast.success("Webhook configuration saved successfully");
      navigate(`/apps/installed/${assistantId}/setting/${settingId}`);
    } catch (error) {
      console.error("Failed to save webhook configuration:", error);
    }
  };

  return (
    <div
      className="container-fluid mt-10 px-4"
      style={{
        paddingLeft: "20rem",
        paddingRight: "20rem",
      }}
    >
      <div className="card">
        <div className="ws-card-header px-4 py-3 d-flex align-items-center gap-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center bg-body-emphasis"
            style={{
              width: 50,
              height: 50,
            }}
          >
            <img
              alt={platformName}
              src={iconSrc}
              style={{
                width: 22,
                height: 22,
              }}
            />
          </div>

          <div>
            <p className="ws-step-heading mb-1">Webhook Setup</p>

            <h5 className="mb-0 fw-bold">{webhookConfig?.title}</h5>
          </div>
        </div>

        <div className="px-4 py-4 ws-panel">
          <p className="ws-label mb-3">SELECT EVENTS</p>

          {events.map((event) => {
            const eventConfig = webhookEvents[event.value];

            return (
              <div
                key={event.value}
                className="border rounded-2 mb-3"
                style={{
                  position: "relative",
                  height: "auto",
                  minHeight: "auto",
                }}
              >
                <div
                  className="d-flex justify-content-between align-items-center"
                  style={{
                    position: "relative",
                    height: "auto",
                    minHeight: "70px",
                    padding: "16px",
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center bg-body-emphasis"
                      style={{
                        width: 20,
                        height: 20,
                        flexShrink: 0,
                      }}
                    >
                      <i className={`wb webconnect-${event.icon} fs-7`} />
                    </div>

                    <div
                      style={{
                        position: "static",
                        display: "block",
                      }}
                    >
                      <h6
                        className="fw-bold"
                        style={{
                          margin: "0 0 3px 0",
                          position: "static",
                        }}
                      >
                        {event.label}
                      </h6>

                      <p
                        className="text-body-secondary fs-9"
                        style={{
                          margin: 0,
                          position: "static",
                        }}
                      >
                        {event.description}
                      </p>
                    </div>
                  </div>

                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={eventConfig?.enabled ?? false}
                      onChange={() => handleEventToggle(event.value)}
                    />
                  </div>
                </div>

                {eventConfig?.enabled && (
                  <div className="border-top p-3">
                    <label className="form-label fs-10 text-uppercase mb-2">
                      Custom Message
                    </label>

                    <textarea
                      className="form-control"
                      rows={2}
                      value={eventConfig?.message ?? ""}
                      onChange={(e) =>
                        handleMessageChange(event.value, e.target.value)
                      }
                    />

                    {event.variables?.length > 0 && (
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {event.variables.map((variable) => (
                          <button
                            key={variable}
                            type="button"
                            className="badge bg-body-secondary text-body border-0"
                            onClick={() =>
                              insertVariable(event.value, variable)
                            }
                          >
                            {`{{${variable}}}`}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="mt-2">
                      <p className="text-uppercase fs-10 text-body-secondary mb-1">
                        Preview
                      </p>

                      <div className="bg-body-secondary rounded-2 px-3 py-2 fs-9">
                        {renderPreview(eventConfig?.message ?? "")}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="ws-card-footer px-4 py-3 d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => handleSave()}
            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Saving...
              </>
            ) : (
              "Save & Continue"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigureWebhook;
